import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, ArrowRight, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import ReviewCarousel from '../components/ReviewCarousel';
import SEO from '../components/SEO';
import JsonLd from '../components/JsonLd';
import { faqPageSchema } from '../lib/schema';

const faqData = [
  {
    id: 1,
    question: "Which areas of Kent do you cover?",
    answer: "Most of our work is within 30–60 minutes of Wye – including Ashford, Canterbury, Faversham, Tenterden, Cranbrook and the surrounding villages. For the right project, especially whole-house redecorations or commercial work, we can travel more widely across Kent by arrangement."
  },
  {
    id: 2,
    question: "Do you offer free quotes, and how do they work?",
    answer: "Yes. We offer free, no-obligation quotes. For anything more than a very small job, we visit in person, talk through what you want, advise on colours and finishes, then send a clear written quote. Where there are several rooms or phases, we usually break the quote down so you can see the cost for each area and prioritise if needed.",
    link: { text: "Request your free quote", to: "/contact" }
  },
  {
    id: 3,
    question: "How much does interior painting typically cost?",
    answer: "Costs depend on the size and condition of the rooms, how much preparation is needed, and the paints and finishes you choose. As a rough guide, a single average-sized room is usually a few hundred pounds, while a full internal refresh of a typical family home runs into the low thousands. We'll always confirm a fixed price before work starts and stick to what we've agreed, unless you decide to add extra areas part-way through.",
    link: { text: "See our interior painting service", to: "/services" }
  },
  {
    id: 4,
    question: "How long will my decorating job take, and how do you work on site?",
    answer: "Smaller rooms are often completed in a day or two, while full houses and larger projects can run from several days to a couple of weeks, depending on size and preparation. We're known for working faster than most decorators without cutting corners: we focus on one job at a time, work steadily through the day, and don't waste time with endless breaks or \"popping off\" to other sites. The aim is minimal disruption, a high-quality finish and getting your home or business back to normal as quickly as possible."
  },
  {
    id: 5,
    question: "What preparation do you do before painting? Do you deal with damp or new plaster?",
    answer: "Preparation is where most of the quality comes from. We fill, sand and prime as needed, deal with stains and problem areas, and protect floors, furniture and fixtures before we start painting. We regularly handle new-builds and fresh plaster, as well as period and listed properties that need more careful treatment and breathable paints. Where there are minor damp issues we can treat and stabilise affected areas before decorating; for more serious damp problems we'll flag them and can work alongside specialists so the finish lasts."
  },
  {
    id: 6,
    question: "Will there be much mess or disruption while you work?",
    answer: "We work tidily and treat your property with respect. Floors and furniture are covered, dust and tools are kept under control, and we tidy at the end of each day. Many of our clients are happy to give us keys while they're at work or away; we lock up securely and send photo and video updates with commentary so you can see exactly what's been done without needing to be on site.",
    externalLink: { text: "Read what customers say on Checkatrade", href: "https://www.checkatrade.com/trades/PaintItBetterLtd" }
  },
  {
    id: 7,
    question: "Are you insured, and do you guarantee your work?",
    answer: "Yes. We carry appropriate insurance for the work we do. We stand behind our workmanship; if there's anything you're not happy with at the end of the job, we'll put it right.",
    externalLink: { text: "View our Checkatrade profile", href: "https://www.checkatrade.com/trades/PaintItBetterLtd" }
  },
  {
    id: 8,
    question: "Do you work with portfolio landlords and managing agents?",
    answer: "Yes. We regularly handle between-tenant refreshes, ongoing maintenance and upgrades for portfolio landlords and managing agents. We understand the need for reliable start dates, quick turnarounds and a consistent standard of finish across multiple properties, so void periods are kept to a minimum."
  },
  {
    id: 9,
    question: "Can you decorate blocks of flats and communal areas?",
    answer: "We can repaint stairwells, lobbies, corridors and other communal spaces in blocks of flats. We plan work to minimise disruption for residents, keep common areas safe and tidy, and can work with your management company on colour schemes and scheduling."
  },
  {
    id: 10,
    question: "Do you take on office and shop refurbishments?",
    answer: "Yes, we take on small commercial projects such as offices, clinics and independent shops. We're used to working around trading hours, staff and customers, and can schedule work in phases or out of hours where needed to keep you open."
  },
  {
    id: 11,
    question: "Can you repair and repaint sash and timber windows?",
    answer: "We can carry out preparation, minor repairs and full repainting of sash and other timber windows to extend their life and improve their appearance. For more serious structural repairs we can coordinate with a specialist joiner, then complete the decorating so everything looks consistent from inside and out."
  },
  {
    id: 12,
    question: "Are you okay working around pets, children and people working from home?",
    answer: "Yes. Many of our clients have young families, pets or work from home. We agree in advance which rooms we're using when, keep doors and gates under control, and plan the schedule so you can carry on with normal life as much as possible."
  },
  {
    id: 13,
    question: "How do you handle hard-to-reach areas and exteriors?",
    answer: "We're experienced with high ceilings, eaves, stairwells and exterior work that needs safe access. For anything beyond what can safely be reached from ladders and internal towers, we work with a trusted scaffolding company so the job is done properly and safely.",
    link: { text: "View our exterior painting service", to: "/services" }
  },
  {
    id: 14,
    question: "How do I know I can trust you in my home?",
    answer: "Most of our work comes from recommendations and repeat clients, and many customers trust us with keys while they're out or even on holiday. We agree in advance how access will work, lock up securely at the end of each day, and send photo and video updates with commentary so you can follow progress from anywhere. Our independent reviews consistently mention that we're punctual, polite, easy to have around and that the property is left clean and tidy.",
    externalLink: { text: "Read our verified reviews", href: "https://www.checkatrade.com/trades/PaintItBetterLtd" }
  },
  {
    id: 15,
    question: "How can I see your reviews and previous projects?",
    answer: "You can see detailed reviews and more project photos on our Checkatrade profile, and we also showcase a selection of recent work in the portfolio section of this website. Together they give a good picture of the types of homes, rental properties and commercial spaces we look after across Kent.",
    link: { text: "View our portfolio", to: "/portfolio" },
    externalLink: { text: "See all reviews on Checkatrade", href: "https://www.checkatrade.com/trades/PaintItBetterLtd" }
  },
  // Clearing and Pressure Cleaning FAQs
  {
    id: 16,
    question: "Do you pressure wash roofs?",
    answer: "Yes, roof cleaning is a key part of our clearing and pressure cleaning service, and it is often the single biggest visual improvement you can make to your home's exterior.",
    link: { text: "See our clearing and cleaning service", to: "/services#pressure-washing" }
  },
  {
    id: 17,
    question: "Is pressure washing safe for my roof tiles?",
    answer: "We always assess the condition and material of your roof first and adjust our methods accordingly, focusing on controlled cleaning rather than simply using maximum pressure on tiles."
  },
  {
    id: 18,
    question: "What other areas can you pressure wash?",
    answer: "Alongside roofs, we can clean driveways, patios, paths, steps, decking and some walls or garden features, as long as the surface is suitable for pressure cleaning."
  },
  {
    id: 19,
    question: "Can you remove all moss and staining from my roof?",
    answer: "We aim to remove as much moss, algae and surface staining as is safely possible, but very old marks or previous damage may not return to a \"brand new\" finish."
  },
  {
    id: 20,
    question: "Will roof pressure washing damage my property?",
    answer: "Used correctly, pressure washing is an effective cleaning method. We take care with pressure levels, angles and access so we clean the roof while avoiding unnecessary wear or disturbance."
  },
  {
    id: 21,
    question: "How long does a typical pressure washing job take?",
    answer: "Many domestic roofs and surrounding areas can be cleaned in a day, but the exact time depends on roof size, access, and whether you also want driveways or patios cleaned at the same visit."
  },
  {
    id: 22,
    question: "How often should I have my roof pressure washed?",
    answer: "Most homeowners find that a professional clean every few years is enough, although roofs in shaded or damp spots, or under trees, may benefit from more regular attention."
  },
  {
    id: 23,
    question: "Can you pressure wash before exterior painting?",
    answer: "Yes, cleaning walls, fascias and nearby hard surfaces before painting can help remove dirt and loose material so paint adheres better and the overall finish looks sharper.",
    link: { text: "See our exterior painting service", to: "/services#exterior-painting" }
  }
];

const FAQItem = ({ item, isOpen, onToggle }) => {
  return (
    <div className="border-b border-slate-200 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full py-5 flex items-start justify-between text-left group"
        aria-expanded={isOpen}
      >
        <h2 className={`text-lg md:text-xl font-semibold pr-4 transition-colors ${
          isOpen ? 'text-cyan-600' : 'text-slate-800 group-hover:text-cyan-600'
        }`}>
          {item.question}
        </h2>
        <span className="flex-shrink-0 mt-1">
          {isOpen ? (
            <ChevronUp className="h-5 w-5 text-cyan-600" />
          ) : (
            <ChevronDown className="h-5 w-5 text-slate-400 group-hover:text-cyan-600 transition-colors" />
          )}
        </span>
      </button>
      
      {/* Answers stay in the page when closed (just hidden), so search engines can read them */}
      <div hidden={!isOpen} className="pb-6 pr-12 animate-in slide-in-from-top-2 duration-200">
          <p className="text-slate-500 text-base leading-relaxed mb-4">
            {item.answer}
          </p>
          <div className="flex flex-wrap gap-4">
            {item.link && (
              <Link 
                to={item.link.to}
                className="inline-flex items-center text-cyan-600 hover:text-cyan-700 text-sm font-medium transition-colors"
              >
                {item.link.text}
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            )}
            {item.externalLink && (
              <a 
                href={item.externalLink.href}
                target="_blank"
                rel="noopener"
                className="inline-flex items-center text-cyan-600 hover:text-cyan-700 text-sm font-medium transition-colors"
              >
                {item.externalLink.text}
                <ArrowRight className="ml-1 h-4 w-4" />
              </a>
            )}
          </div>
      </div>
    </div>
  );
};

const FAQ = () => {
  const [openItems, setOpenItems] = useState(new Set([1])); // First item open by default

  const toggleItem = (id) => {
    setOpenItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setOpenItems(new Set(faqData.map(item => item.id)));
  };

  const collapseAll = () => {
    setOpenItems(new Set());
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-cyan-50">
      <SEO 
        title="Painter and Decorator FAQs in Kent | Paint It Better"
        description="Answers to common questions about hiring a painter and decorator in Kent, from costs and timings to how we work in your home. Get the info you need."
        canonical="/faq"
      />
      <JsonLd data={faqPageSchema(faqData)} />
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-cyan-600 to-teal-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Painter and Decorator FAQs in Kent
            </h1>
            <p className="text-xl text-cyan-100">
              Got questions about hiring a painter and decorator? Find answers to the most common questions from Kent homeowners below.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Controls */}
            <div className="flex justify-end gap-4 mb-8">
              <button
                onClick={expandAll}
                className="text-sm text-cyan-600 hover:text-cyan-700 font-medium transition-colors"
              >
                Expand all
              </button>
              <span className="text-slate-300">|</span>
              <button
                onClick={collapseAll}
                className="text-sm text-cyan-600 hover:text-cyan-700 font-medium transition-colors"
              >
                Collapse all
              </button>
            </div>

            {/* FAQ Items */}
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
              {faqData.map((item) => (
                <FAQItem
                  key={item.id}
                  item={item}
                  isOpen={openItems.has(item.id)}
                  onToggle={() => toggleItem(item.id)}
                />
              ))}
            </div>

            {/* Still Have Questions */}
            <div className="mt-12 text-center bg-slate-50 rounded-2xl p-8">
              <HelpCircle className="h-12 w-12 text-cyan-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                Still have questions?
              </h2>
              <p className="text-slate-600 mb-6">
                Can't find the answer you're looking for? Get in touch and we'll be happy to help.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full px-8 transition-all duration-300 hover:scale-105">
                    Contact Us
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a 
                  href="https://www.checkatrade.com/trades/PaintItBetterLtd"
                  target="_blank"
                  rel="noopener"
                >
                  <Button size="lg" variant="outline" className="border-2 border-cyan-600 text-cyan-600 hover:bg-cyan-50 rounded-full px-8 transition-all duration-300">
                    View Checkatrade Profile
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-600 to-teal-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Transform Your Home?
            </h2>
            <p className="text-xl mb-8 text-cyan-100">
              Contact Paint It Better today for a free, no-obligation quote on your painting and decorating project in Kent.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-cyan-600 hover:bg-cyan-50 px-8 py-6 text-lg rounded-full transition-all duration-300 hover:scale-105 shadow-xl">
                Get Your Free Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Review Carousel */}
      <ReviewCarousel />
    </div>
  );
};

export default FAQ;
